/**
 * Camera-shutter sound effect, loaded once per session and re-triggered on
 * every `save_picture`. Plays through the existing user-gesture-bound
 * AudioContext (the same one the LiveSession uses for the mic graph), so no
 * additional gesture is required.
 *
 * The clip lives at `/shutter.ogg` (`web/public/shutter.ogg`, Opus in an
 * Ogg container). It is fetched + decoded once at session start; each
 * playback creates a fresh AudioBufferSourceNode (the WebAudio idiom —
 * buffers are reusable, sources are single-use).
 */
const SHUTTER_URL = '/shutter.ogg'

export type ShutterPlayFn = () => void

/**
 * Load the shutter sound. Returns a sync play function once the buffer is
 * decoded. The caller should start the load early (on grant mount) and
 * call the returned function on each save_picture; if the audio file fails
 * to load (offline, missing asset), the returned play function is a no-op.
 */
export const createShutter = async (
  ctx: AudioContext,
  fetchImpl: typeof fetch = fetch,
): Promise<ShutterPlayFn> => {
  try {
    const res = await fetchImpl(SHUTTER_URL)
    if (!res.ok) throw new Error(`shutter fetch ${res.status}`)
    const arrayBuf = await res.arrayBuffer()
    const audioBuf = await ctx.decodeAudioData(arrayBuf)
    return (): void => {
      try {
        const src = ctx.createBufferSource()
        src.buffer = audioBuf
        src.connect(ctx.destination)
        src.start(0)
      } catch {
        /* swallow — shutter tick is purely affordance */
      }
    }
  } catch (e) {
    console.warn('[shutter] failed to load click asset:', e)
    return (): void => { /* no-op fallback */ }
  }
}
